import { useQuery } from '@tanstack/react-query';
import { userModule } from '../api/modules/user.module';

// Query key factory
export const sellerProfileKeys = {
  all: ['sellerProfile'],
  detail: () => [...sellerProfileKeys.all, 'detail'],
};

/**
 * Fetches the current user's seller profile via /api/users/profile/get-seller-profile/.
 *
 * The endpoint responds with { success, data: { role, verified, store_name, ... } }.
 * `verified` is true only once an admin approves the seller (status === "approved");
 * `store_name` is present whenever a seller profile exists (even while pending).
 * A plain buyer with no seller profile gets store_name = null.
 *
 * Pass { enabled: !isSeller } from the caller to skip the request for users
 * already known to be sellers.
 */
export const useSellerProfile = (options = {}) => {
  return useQuery({
    queryKey: sellerProfileKeys.detail(),
    queryFn: async () => {
      const response = await userModule.getSellerProfile();
      if (!response.success) {
        throw new Error(response.message);
      }
      // userModule wraps the axios body in `data`; the profile sits under data.data.
      return response.data?.data ?? response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * True when the user has registered as a seller but isn't approved yet:
 * a seller profile exists (store_name) but it isn't verified and the role
 * hasn't been flipped to "seller".
 */
export const isPendingSellerProfile = (profile) =>
  !!profile?.store_name && profile?.verified !== true && profile?.role !== 'seller';
